
const stripHtmlTags = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') 
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n') 
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const processHtmlContent = (html) => {
  if (!html) return [];
  
  const elements = [];
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  const processNode = (node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tagName = node.tagName.toLowerCase();
      
      if (/^h[1-6]$/.test(tagName)) {
        const text = node.textContent.trim();
        if (text) {
          elements.push({
            type: 'heading',
            level: tagName,
            text: text
          });
        }
      }
      else if (tagName === 'ul' || tagName === 'ol') {
        const items = [];
        const listItems = node.querySelectorAll(':scope > li');
        listItems.forEach(li => {
          const text = li.textContent.trim();
          if (text) items.push(text);
        });
        
        if (items.length > 0) {
          elements.push({
            type: 'list',
            listType: tagName === 'ol' ? 'ordered' : 'unordered',
            items: items
          });
        }
      }
      else if (tagName === 'a') {
        const href = node.getAttribute('href');
        const text = node.textContent.trim();
        if (href && text) {
          elements.push({
            type: 'link',
            url: href,
            text: text
          });
        }
      }
      else if (tagName === 'p' || tagName === 'div') {
        const hasSpecialChildren = node.querySelector('h1, h2, h3, h4, h5, h6, ul, ol, a');
        
        if (!hasSpecialChildren) {
          const text = node.textContent.trim();
          if (text) {
            elements.push({
              type: 'text',
              text: text
            });
          }
        } else {
          Array.from(node.childNodes).forEach(child => processNode(child));
        }
      }
      else {
        Array.from(node.childNodes).forEach(child => processNode(child));
      }
    }
    else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent.trim();
      if (text) {
        elements.push({
          type: 'text',
          text: text
        });
      }
    }
  };
  
  Array.from(tempDiv.childNodes).forEach(child => processNode(child));
  
  return elements;
};

const processHtmlContentFallback = (html) => {
  if (!html) return [];
  
  const elements = [];
  let remaining = html;
  
  const patterns = [
    { 
      regex: /<h([1-6])[^>]*>(.*?)<\/h\1>/i,
      handler: (match) => ({
        type: 'heading',
        level: `h${match[1]}`,
        text: stripHtmlTags(match[2])
      })
    },
    {
      regex: /<(ul|ol)[^>]*>(.*?)<\/\1>/is,
      handler: (match) => {
        const listType = match[1];
        const items = [];
        const liRegex = /<li[^>]*>(.*?)<\/li>/gi;
        let liMatch;
        
        while ((liMatch = liRegex.exec(match[2])) !== null) {
          const text = stripHtmlTags(liMatch[1]);
          if (text) items.push(text);
        }
        
        if (items.length > 0) {
          return {
            type: 'list',
            listType: listType === 'ol' ? 'ordered' : 'unordered',
            items: items
          };
        }
        return null;
      }
    },
    {
      regex: /<a[^>]+href=["']([^"']+)["'][^>]*>(.*?)<\/a>/i,
      handler: (match) => ({
        type: 'link',
        url: match[1],
        text: stripHtmlTags(match[2])
      })
    },
    {
      regex: /<p[^>]*>(.*?)<\/p>/i,
      handler: (match) => {
        const text = stripHtmlTags(match[1]);
        if (text) {
          return {
            type: 'text',
            text: text
          };
        }
        return null;
      }
    },
    {
      regex: /<div[^>]*>(.*?)<\/div>/i,
      handler: (match) => {
        const text = stripHtmlTags(match[1]);
        if (text) {
          return {
            type: 'text',
            text: text
          };
        }
        return null;
      }
    }
  ];
  
  const matches = [];
  
  patterns.forEach(pattern => {
    const regex = new RegExp(pattern.regex.source, pattern.regex.flags + 'g');
    let match;
    
    while ((match = regex.exec(html)) !== null) {
      const element = pattern.handler(match);
      if (element) {
        matches.push({
          index: match.index,
          element: element
        });
      }
    }
  });
  
  matches.sort((a, b) => a.index - b.index);
  
  return matches.map(m => m.element);
};

const processModuleParams = (params) => {
  if (!params) return [];
  
  const elements = [];
  
  if (params.richTextContentHTML || params.html) {
    const html = params.richTextContentHTML || params.html;
    
    try {
      if (typeof document !== 'undefined') {
        elements.push(...processHtmlContent(html));
      } else {
        elements.push(...processHtmlContentFallback(html));
      }
    } catch (e) {
      elements.push(...processHtmlContentFallback(html));
    }
  }
  
  if (params.headingAndTextHeading) {
    const level = params.headingAndTextHeadingLevel || params.groupStyle?.headingStyleVariant || 'h2';
    elements.push({
      type: 'heading',
      level: level,
      text: params.headingAndTextHeading
    });
  }
  
  if (params.groupListItems && Array.isArray(params.groupListItems)) {
    const items = params.groupListItems
      .map(item => item.groupListContent?.listItemContent)
      .filter(Boolean);
    
    if (items.length > 0) {
      elements.push({
        type: 'list',
        listType: 'unordered',
        items: items
      });
    }
  }
  
  if (params.buttonContentText || params.groupButtons) {
    if (params.buttonContentText) {
      const url = params.buttonContentLink?.url?.href || '';
      if (url) {
        elements.push({
          type: 'link',
          url: url,
          text: params.buttonContentText
        });
      }
    }
    
    if (params.groupButtons && Array.isArray(params.groupButtons)) {
      params.groupButtons.forEach(button => {
        const url = button.buttonContentLink?.url?.href || '';
        const text = button.buttonContentText || '';
        if (url && text) {
          elements.push({
            type: 'link',
            url: url,
            text: text
          });
        }
      });
    }
  }
  
  if (params.img) {
    const alt = params.img.alt;
    if (alt) {
      elements.push({
        type: 'text',
        text: alt
      });
    }
  }
  
  if (params.content?.content) {
    const html = params.content.content;
    try {
      if (typeof document !== 'undefined') {
        elements.push(...processHtmlContent(html));
      } else {
        elements.push(...processHtmlContentFallback(html));
      }
    } catch (e) {
      elements.push(...processHtmlContentFallback(html));
    }
  }
  
  return elements;
};


const processLayoutSections = (layoutSections) => {
  if (!layoutSections) return [];
  
  const allElements = [];
  
  const processCell = (cell) => {
    if (!cell) return;
    
    if (cell.params) {
      const elements = processModuleParams(cell.params);
      if (elements && elements.length > 0) {
        allElements.push(...elements);
      }
    }
    
    if (cell.rows && Array.isArray(cell.rows)) {
      cell.rows.forEach(row => {
        Object.values(row).forEach(nestedCell => {
          processCell(nestedCell);
        });
      });
    }
    
    if (cell.cells && Array.isArray(cell.cells)) {
      cell.cells.forEach(nestedCell => {
        processCell(nestedCell);
      });
    }
  };
  
  Object.values(layoutSections).forEach(section => {
    if (section.rows && Array.isArray(section.rows)) {
      section.rows.forEach(row => {
        Object.values(row).forEach(cell => {
          processCell(cell);
        });
      });
    }
  });
  
  return allElements;
};

const processWidgets = (widgets) => {
  if (!widgets) return [];
  
  const allElements = [];
  
  Object.values(widgets).forEach(widget => {
    if (widget.body) {
      const elements = processModuleParams({ html: widget.body.value });
      if (elements && elements.length > 0) {
        allElements.push(...elements);
      }
    }
  });
  
  return allElements;
};


const processWidgetContainers = (widgetContainers) => {
  if (!widgetContainers) return [];
  
  const allElements = [];
  
  Object.values(widgetContainers).forEach(container => {
    if (container.widgets) {
      Object.values(container.widgets).forEach(widget => {
        if (widget.body) {
          const elements = processModuleParams({ html: widget.body.value });
          if (elements && elements.length > 0) {
            allElements.push(...elements);
          }
        }
      });
    }
  });
  
  return allElements;
};

const elementsToHtml = (elements) => {
  if (!elements || elements.length === 0) return '';
  
  let html = '';
  
  elements.forEach(element => {
    switch (element.type) {
      case 'heading':
        html += `<${element.level}>${element.text}</${element.level}>\n`;
        break;
        
      case 'text':
        if (element.text.trim()) {
          html += `<p>${element.text}</p>\n`;
        }
        break;
        
      case 'list':
        const tag = element.listType === 'ordered' ? 'ol' : 'ul';
        html += `<${tag}>\n`;
        element.items.forEach(item => {
          html += `  <li>${item}</li>\n`;
        });
        html += `</${tag}>\n`;
        break;
        
      case 'link':
        html += `<a href="${element.url}">${element.text}</a>\n`;
        break;
    }
  });
  
  return html.trim();
};


export const extractHubSpotPageContent = (page) => {
  const allElements = [];
  
  if (page.layoutSections) {
    const layoutElements = processLayoutSections(page.layoutSections);
    if (layoutElements && layoutElements.length > 0) {
      allElements.push(...layoutElements);
    }
  }
  
  if (page.widgets) {
    const widgetElements = processWidgets(page.widgets);
    if (widgetElements && widgetElements.length > 0) {
      allElements.push(...widgetElements);
    }
  }
  
  if (page.widgetContainers) {
    const containerElements = processWidgetContainers(page.widgetContainers);
    if (containerElements && containerElements.length > 0) {
      allElements.push(...containerElements);
    }
  }
  
  return elementsToHtml(allElements);
};

export const extractHubSpotPageContentDebug = (page) => {
  const allElements = [];
  
  if (page.layoutSections) {
    const layoutElements = processLayoutSections(page.layoutSections);
    if (layoutElements && layoutElements.length > 0) {
      allElements.push(...layoutElements);
    }
  }
  
  if (page.widgets) {
    const widgetElements = processWidgets(page.widgets);
    if (widgetElements && widgetElements.length > 0) {
      allElements.push(...widgetElements);
    }
  }
  
  if (page.widgetContainers) {
    const containerElements = processWidgetContainers(page.widgetContainers);
    if (containerElements && containerElements.length > 0) {
      allElements.push(...containerElements);
    }
  }
  
  return {
    elements: allElements,
    html: elementsToHtml(allElements)
  };
};